import type {
  AvailabilitySlotListModel,
  AvailabilitySlotModel,
  AvailabilitySlotPostModel,
  AvailabilitySlotPutModel,
} from '../app/types/availability-slot.type.ts';
import baseApi, { CacheTag } from './meeting-md-api-slice';

const availabilitySlotApiSlice = baseApi.injectEndpoints({
  endpoints: builder => ({
    fetchFutureForAccountAvailabilitySlots: builder.query<AvailabilitySlotListModel[], string>({
      query: accountKey => ({ url: `/AvailabilitySlot/Future/${accountKey}` }),
      providesTags: [CacheTag.Events],
    }),
    fetchAvailabilitySlot: builder.query<AvailabilitySlotModel, string>({
      query: key => ({ url: `/AvailabilitySlot/${key}` }),
      providesTags: [CacheTag.Events],
    }),
    fetchReservedForRepresentativeAvailabilitySlots: builder.query<
      AvailabilitySlotListModel[],
      void
    >({
      query: () => ({ url: `/AvailabilitySlot/Reserved` }),
      providesTags: [CacheTag.Events],
    }),
    postAvailabilitySlot: builder.mutation<void, AvailabilitySlotPostModel>({
      query: model => ({
        url: 'AvailabilitySlot',
        method: 'POST',
        body: model,
      }),
      invalidatesTags: [CacheTag.Events],
    }),
    deleteAvailabilitySlot: builder.mutation<void, string>({
      query: key => ({ url: `/AvailabilitySlot/${key}`, method: 'DELETE' }),
      invalidatesTags: [CacheTag.Events],
    }),
    deleteRecurringAvailabilitySlotSeries: builder.mutation<
      void,
      { availabilitySlotRecurringSeriesKey: string; minStart?: string }
    >({
      query: ({ availabilitySlotRecurringSeriesKey, minStart }) => {
        const params = new URLSearchParams();

        if (minStart) {
          params.append('minStart', minStart);
        }

        return {
          url: `/AvailabilitySlot/RecurringSeries/${availabilitySlotRecurringSeriesKey}?${params.toString()}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: [CacheTag.Events],
    }),
    putAvailabilitySlot: builder.mutation<
      AvailabilitySlotModel,
      { key: string; model: AvailabilitySlotPutModel }
    >({
      query: ({ key, model }) => ({
        url: `/AvailabilitySlot/${key}`,
        method: 'PUT',
        body: model,
      }),
      invalidatesTags: [CacheTag.Events],
    }),
    fetchDaysOfWeekList: builder.query<string[], void>({
      query: () => ({ url: '/AvailabilitySlot/DaysOfWeekList' }),
    }),
  }),
});

export const {
  useFetchAvailabilitySlotQuery,
  useFetchFutureForAccountAvailabilitySlotsQuery,
  useFetchReservedForRepresentativeAvailabilitySlotsQuery,
  usePostAvailabilitySlotMutation,
  useDeleteAvailabilitySlotMutation,
  useDeleteRecurringAvailabilitySlotSeriesMutation,
  usePutAvailabilitySlotMutation,
  useFetchDaysOfWeekListQuery,
} = availabilitySlotApiSlice;
